/**
 * The cap field keeps the trader's text and fails closed: anything that is not
 * a whole number 1 or more clears the stored cap, so sizing never runs at a
 * value the field does not show (as in TwoSidedMultiplierSetting, #607), and
 * one line says why. Blank is off.
 *
 * Sizing that a blank or invalid cap paused resumes only at the next valid cap
 * of the same focused edit, and only if nothing else wrote this trader's
 * settings meanwhile: a blur or an outside write ends that intent.
 */
import { useId, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useBookLiquiditySettings, validLiquidityCap, type BookLiquidityPreference } from '@/stores/bookLiquiditySettings';

const WHOLE_NUMBER = /^\d+$/;
const parseCap = (raw: string) => (WHOLE_NUMBER.test(raw) && validLiquidityCap(Number(raw)) ? Number(raw) : null);

interface Draft {
  text: string;
  /** The stored cap this text left behind: any other cap shows instead. */
  basis: string;
  userId: string;
  /** The exact settings this edit wrote. Every write makes a new object, so a
   *  write from anywhere else, even of the same values, ends the resume. */
  wrote: BookLiquidityPreference | undefined;
  resume: boolean;
}

export function BookLiquiditySettings() {
  const userId = useAuthStore(s => s.user?.id);
  return <TraderBookLiquiditySettings key={userId ?? 'signed-out'} userId={userId} />;
}

function TraderBookLiquiditySettings({ userId }: { userId: string | undefined }) {
  const settings = useBookLiquiditySettings(s => userId ? s.traders[userId] : undefined);
  const update = useBookLiquiditySettings(s => s.update);
  const cap = settings?.cap ?? null;
  const stored = cap === null ? '' : String(cap);
  const [draft, setDraft] = useState<Draft | null>(null);
  const live = draft && draft.userId === userId && draft.basis === stored ? draft : null;
  const resumable = live !== null && live.resume && live.wrote === settings;
  const text = live ? live.text : stored;
  const invalid = text !== '' && parseCap(text) === null;
  const messageId = useId();
  return <div className="flex flex-wrap items-center gap-3">
    <label className="flex items-center gap-2 text-xs">
      <input type="checkbox" checked={settings?.enabled ?? false} disabled={!userId || cap === null}
        onChange={e => { if (userId) update(userId, { enabled: e.target.checked, cap }); }} />
      Size to book liquidity
    </label>
    <label className="flex flex-wrap items-center gap-2 text-xs">
      Maximum autofill contracts
      <input type="text" inputMode="numeric" autoComplete="off" value={text} placeholder="Set cap"
        aria-invalid={invalid} aria-describedby={invalid ? messageId : undefined}
        className={`bg-input w-28 rounded-md border px-2.5 py-1.5 font-mono text-xs ${invalid ? 'border-amber-500' : 'border-border'}`}
        onChange={e => {
          if (!userId) return;
          // Raw, untrimmed: a stray space is invalid, never silently dropped.
          const raw = e.target.value;
          const next = parseCap(raw);
          const resume = resumable || (settings?.enabled ?? false);
          update(userId, next === null ? { enabled: false, cap: null } : { enabled: resume, cap: next });
          setDraft({
            text: raw,
            basis: next === null ? '' : String(next),
            userId,
            wrote: useBookLiquiditySettings.getState().traders[userId],
            resume: next === null && resume,
          });
        }}
        // An invalid draft stays visible after the edit, but no longer resumes sizing.
        onBlur={() => setDraft(invalid && live ? { ...live, resume: false } : null)} />
    </label>
    {invalid && <p id={messageId} className="basis-full text-xs text-amber-500 light:text-amber-700">
      Not saved — enter a whole number 1 or more.{resumable ? ' Sizing is paused until then.' : ''}
    </p>}
  </div>;
}
